// ink-component
import React from 'react';
import { ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';

import useResponsiveNav from '../_common/hooks/useResponsiveNav';
import DefaultMoreDropdown from '../_common/DefaultMoreDropdown/DefaultMoreDropdown';
import NavWrapper from '../_common/NavWrapper/NavWrapper';

import HorizontalNavAnchor from './components/HorizontalNavAnchor/HorizontalNavAnchor';
import HorizontalNavBelt from './components/HorizontalNavBelt/HorizontalNavBelt';
import HorizontalNavButton from './components/HorizontalNavButton/HorizontalNavButton';
import HorizontalNavItem from './components/HorizontalNavItem/HorizontalNavItem';

export interface HorizontalNavProps extends React.HTMLAttributes<HTMLUListElement> {
  /** alignment of HorizontalNav */
  align?: 'left' | 'center';
  /** Adds left and right gutter spacing. Should only be used to establish precedence over page or frame context. */
  hasSidePadding?: boolean;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Horizontally wraps a set of clickable elements for navigation
 * @group HorizontalNav
 * @categories navigation
 * @aka nav
 */
function HorizontalNav({ style, className, children, align, hasSidePadding, ...otherProps }: HorizontalNavProps) {
  const [navItems, dropdownItems, navRef] = useResponsiveNav(React.Children.toArray(children));

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        isHorizontalNav: true,
        navWrapperAlign: align,
        navWrapperHasSidePadding: hasSidePadding,
      })}
    >
      <NavWrapper role="navigation" {...otherProps} {...globalProps} ref={navRef}>
        {navItems}
        {dropdownItems.length > 0 && <DefaultMoreDropdown items={dropdownItems} trigger={HorizontalNavButton} />}
      </NavWrapper>
    </ThemeProvider>
  );
}

HorizontalNav.Anchor = HorizontalNavAnchor;
HorizontalNav.Belt = HorizontalNavBelt;
HorizontalNav.Button = HorizontalNavButton;
HorizontalNav.Item = HorizontalNavItem;

export { HorizontalNavAnchorProps } from './components/HorizontalNavAnchor/HorizontalNavAnchor';
export { HorizontalNavBeltProps } from './components/HorizontalNavBelt/HorizontalNavBelt';
export { HorizontalNavButtonProps } from './components/HorizontalNavButton/HorizontalNavButton';
export { HorizontalNavItemProps } from './components/HorizontalNavItem/HorizontalNavItem';

HorizontalNav.defaultProps = {
  align: 'left',
  hasSidePadding: false,
};

HorizontalNav.displayName = 'HorizontalNav';

export default HorizontalNav;
